import { useState, useCallback, useRef, useEffect } from "react";
import { GetServiceRequestByOrganization } from "../GetServiceRequestByOrganization";
import { extractPatients } from "../../condition/hooks/useConditionByOrganization";

interface UseServiceRequestByOrganizationProps {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Status (ex: active, completed) */
  status?: string | string[];
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter no bundle */
  includeEncounter?: boolean;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
  /** Incluir Patient no bundle */
  includePatient?: boolean;
}

interface UseServiceRequestByOrganizationResult {
  data: {
    serviceRequests: fhir5.ServiceRequest[];
    total?: number;
    patients?: fhir5.Patient[];
  };
  loading: boolean;
  error: Error | null;
  fetchServiceRequests: () => Promise<void>;
}

function extractServiceRequests(
  bundle?: fhir5.Bundle
): fhir5.ServiceRequest[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.ServiceRequest> =>
        entry.resource?.resourceType === "ServiceRequest"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.ServiceRequest => resource !== undefined
    );
}

/**
 * Filtra ServiceRequests que possuem o texto TUSSProcedimentosEventos em qualquer lugar do objeto
 * @param serviceRequests Array de ServiceRequest para filtrar
 * @returns Array filtrado de ServiceRequest que contém o texto TUSSProcedimentosEventos
 */
export function filterServiceRequestsByTUSSProcedimentosEventos(
  serviceRequests: fhir5.ServiceRequest[]
): fhir5.ServiceRequest[] {
  const searchText = "TUSSProcedimentosEventos";
  
  return serviceRequests.filter((serviceRequest) => {
    // Verifica se tem o campo code preenchido
    if (!serviceRequest.code) return false;
    
    // Verifica se tem coding com o sistema TUSS
    const hasTUSSCoding = serviceRequest.code.concept?.coding?.some(
      (coding: any) => coding.system === "https://fluxmed.com.br/fhir/r5/CodeSystem/TUSSProcedimentosEventos"
    );
    
    if (hasTUSSCoding) return true;
    
    // Fallback: procura o texto em todo o objeto
    const jsonString = JSON.stringify(serviceRequest);
    return jsonString.includes(searchText);
  });
}

export function useServiceRequestByOrganization(
  props: UseServiceRequestByOrganizationProps
): UseServiceRequestByOrganizationResult {
  const [data, setData] = useState<{
    serviceRequests: fhir5.ServiceRequest[];
    patients?: fhir5.Patient[];
    total?: number;
  }>({
    serviceRequests: [],
    patients: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramsRef = useRef(props);

  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchServiceRequests = useCallback(async () => {
    const currentParams = paramsRef.current;
    if (!currentParams.organizationId) return;
    try {
      setLoading(true);
      setError(null);
      
      // Primeiro busca o count total
      const countResponse = await GetServiceRequestByOrganization({
        ...currentParams,
        summary: true,
        count: 0,
      });
      
      const totalCount = countResponse.total || 0;
      console.log(`Total de ServiceRequest encontrados: ${totalCount}`);
      
      // Agora busca todos os registros com o count adequado
      const response = await GetServiceRequestByOrganization({
        ...currentParams,
        count: totalCount > 0 ? totalCount : 1000, // Usa o total encontrado ou um valor padrão alto
        summary: false
      });
      
      const serviceRequests = extractServiceRequests(response);
      const patients = extractPatients(response);
      setData({ serviceRequests, patients, total: totalCount });
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar ServiceRequest")
      );
      console.error("useServiceRequestByOrganization error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchServiceRequests };
}
